import { useEffect, useState } from 'react';
import * as cookieHelper from '../lib/cookieHelper';
import { SESSION_DEBUG_MODE, DEBUG_SIGNIN_SESSION_KEY } from '../lib/sessionDebug';
import styles from '../styles/SessionDebugPanel.module.css';

const SessionDebugPanel = () => {
  const [cookies, setCookies] = useState({});
  const [reloadSessionRaw, setReloadSessionRaw] = useState('');
  const [reloadStatus, setReloadStatus] = useState('');
  const [parsedAiChat, setParsedAiChat] = useState('(n/a)');
  const [signinSessionRaw, setSigninSessionRaw] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const refreshAll = async () => {
    setCookies(cookieHelper.getCookie() || {});
    setSigninSessionRaw(sessionStorage.getItem(DEBUG_SIGNIN_SESSION_KEY) || '');

    setLoading(true);
    setError('');
    try {
      const result = await cookieHelper.fetchReloadSessionRaw();
      setReloadStatus(`${result.status}${result.ok ? '' : ' (failed)'}`);
      setReloadSessionRaw(result.body || '(empty response)');
      setParsedAiChat(
        result.parsed && result.parsed.zd_aichat !== undefined
          ? String(result.parsed.zd_aichat)
          : '(missing in parsed body)'
      );
      // Cookies may have been rewritten by reload_session.php
      setCookies(cookieHelper.getCookie() || {});
    } catch (e) {
      setError(String(e?.message || e));
      setReloadSessionRaw('');
      setParsedAiChat('(error)');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!SESSION_DEBUG_MODE || typeof window === 'undefined') {
      return undefined;
    }

    refreshAll();

    const handleSignIn = () => refreshAll();
    const handleSignOff = () => refreshAll();

    document.addEventListener('signin', handleSignIn);
    document.addEventListener('signoff', handleSignOff);

    return () => {
      document.removeEventListener('signin', handleSignIn);
      document.removeEventListener('signoff', handleSignOff);
    };
  }, []);

  if (!SESSION_DEBUG_MODE) {
    return null;
  }

  const cookieEntries = Object.entries(cookies).sort(([a], [b]) => a.localeCompare(b));
  const zdCookies = cookieEntries.filter(([key]) => key.startsWith('zd_'));

  return (
    <div className={styles.root}>
      <div className={styles.panel}>
        <div className={styles.panelHeader}>
          <span>Session Debug</span>
          <button type="button" className={styles.refreshBtn} onClick={refreshAll} disabled={loading}>
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>

        <div className={styles.highlight}>
          <span className={styles.key}>zd_aichat (cookie)</span>
          <span className={styles.value}>
            {cookies.zd_aichat === undefined ? '(missing)' : String(cookies.zd_aichat)}
          </span>
        </div>

        <div className={styles.highlight}>
          <span className={styles.key}>zd_aichat (parsed body)</span>
          <span className={styles.value}>{parsedAiChat}</span>
        </div>

        <div className={styles.section}>
          <div className={styles.sectionTitle}>
            Server: reload_session.php {reloadStatus ? `[${reloadStatus}]` : ''}
          </div>
          <pre className={styles.raw}>{error || reloadSessionRaw || '(no data yet)'}</pre>
        </div>

        <div className={styles.section}>
          <div className={styles.sectionTitle}>Server: signin_session_token.php (last login)</div>
          <pre className={styles.raw}>{signinSessionRaw || '(not captured yet — login once while debug is on)'}</pre>
        </div>

        <div className={styles.section}>
          <div className={styles.sectionTitle}>Browser cookies (zd_*)</div>
          <div className={styles.list}>
            {(zdCookies.length ? zdCookies : cookieEntries).map(([key, value]) => (
              <div key={key} className={styles.row}>
                <span className={styles.key}>{key}</span>
                <span className={styles.value}>{String(value)}</span>
              </div>
            ))}
            {cookieEntries.length === 0 && (
              <div className={styles.empty}>No cookies found</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionDebugPanel;
